/* eslint-disable i18next/no-literal-string */
import { forwardRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { MenuItem, Select, SelectProps } from 'ui';

export interface SelectOption {
  value: string | number | undefined;
  label: string;
}

export type SelectFieldProps = SelectProps & {
  options: SelectOption[];
  name: string;
  label: string;
  autocomplete?: string;
  'data-testid'?: string;
};

export const SelectField = forwardRef(function SelectField(
  {
    label,
    name,
    autocomplete,
    options,
    disabled = false,
    required = false,
    helperText,
    size = 'small',
    onChange,
    ...rest
  }: SelectFieldProps,
  ref
) {
  const { control } = useFormContext();

  return (
    <Controller
      control={control}
      name={name}
      rules={
        required
          ? {
              required: 'This field is required',
            }
          : {}
      }
      render={({ field, fieldState }) => {
        const { error } = fieldState;
        const helperTextContent = error ? String(error?.message) : helperText;
        return (
          <Select
            id={name}
            error={Boolean(error)}
            label={label}
            disabled={disabled}
            required={required}
            {...field}
            {...rest}
            size={size}
            value={field.value || ''}
            name={autocomplete || name}
            ref={ref || field.ref}
            helperText={helperTextContent}
            onChange={(event, child) => {
              if (!event.target.value) {
                field.onChange(undefined);
              } else {
                field.onChange(event.target.value);
              }
              onChange?.(event, child);
            }}
          >
            {options.map((option: SelectOption) => (
              <MenuItem key={option.label} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        );
      }}
    />
  );
});
